<template>
  <div>
    <v-container>
      <v-card
        :loading="!isLoaded"
      >
        <v-overlay
          :absolute="true"
          :value="isComponentLoading"
        >
          <v-progress-circular indeterminate size="24"></v-progress-circular>
        </v-overlay>
        <filter-daily-reports
          :default-search-params="searchParams"
          :is-component-loading="!isLoaded"
          @filtering="onFilter"
        ></filter-daily-reports>
        <v-row class="pa-2">
          <v-col>
            <section class="main" v-cloak>
              <div
                v-if="searchParams.manager && searchParams.from"
                class="d-flex justify-end"
              >
                <v-btn
                  icon
                  @click="toggleEditReport"
                >
                  <v-icon color="pink lighten-1">mdi-pencil</v-icon>
                </v-btn>
              </div>

              <template
                v-if="isEditReport && id"
              >
                <div
                  class="todo-list"
                >
                  <div class="d-flex align-center">
                    <input
                      id="toggle-all"
                      class="toggle-all mr-2"
                      type="checkbox"
                      v-model="allDone"
                    />
                    <label for="toggle-all"></label>
                    <input
                      class="new-todo w-100"
                      autofocus
                      autocomplete="off"
                      :placeholder="$t('reports.what_need_todo')+'?'"
                      v-model="newTodo"
                      @keyup.enter="addTodo"
                    />
                  </div>
                  <v-divider class="my-2"></v-divider>
                  <div class="todo-list">
                    <div
                      v-for="todo in filteredTodos"
                      class="todo d-flex mb-2"
                      :key="todo.id"
                      :class="{ completed: todo.completed, editing: todo == editedTodo }"
                    >
                      <div class="mr-2 mt-6">
                        <input class="toggle" type="checkbox" v-model="todo.completed"/>
                      </div>
                      <div class="grow">
                        <v-text-field
                          class="edit w-100"
                          type="text"
                          :value="todo.title"
                          @change="todo.title = $event;updateRequest()"
                        />
                        <v-text-field
                          class="edit w-100"
                          type="text"
                          :value="todo.note"
                          @change="todo.note = $event;updateRequest()"
                          :placeholder="$t('reports.comment')"
                        />
                      </div>
                      <div>
                        <v-btn
                          icon
                          fab
                          dark
                          small
                          color="pink"
                          @click.prevent="removeTodo(todo)"
                        >
                          <v-icon
                          >mdi-trash-can-outline
                          </v-icon>
                        </v-btn>
                      </div>
                    </div>
                  </div>
                  <div>
                    {{$t('reports.alert')}}
                  </div>
                </div>
              </template>
              <template
                v-else
              >
                <div
                  v-if="id"
                >
                  <div
                    v-if="filteredTodos.length"
                  >
                    <div
                      v-for="todo in filteredTodos"
                      class="todo"
                      :key="todo.id"
                      :class="{ completed: todo.completed, editing: todo == editedTodo }"
                    >
                      <v-divider class="mb-2"></v-divider>
                      <div class="d-flex">
                        <div class="mr-2">
                          <input class="toggle" type="checkbox" :checked="todo.completed" disabled/>
                        </div>
                        <div class="grow">
                          <p>
                            {{ todo.title }}
                          </p>
                          <p
                            v-if="todo.note"
                            class="v-list-item__subtitle">
                            {{ todo.note }}
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div
                    v-else
                    class="text-center"
                  >
                    {{$t('reports.report_empty')}}
                  </div>
                </div>
                <div
                  v-else
                  class="text-center"
                >
                  {{$t('reports.report_not_found')}}
                </div>
              </template>
            </section>
          </v-col>
        </v-row>
      </v-card>
    </v-container>
  </div>
</template>

<script>
import Vue from 'vue';
import moment from 'moment';
import updatePageContentMixin from '@/mixins/updatePageContentMixin';
import ManagerDailyReportProxy from '@/proxies/Manager/ManagerDailyReportProxy';
import FilterDailyReports from '@/components/listings/FilterDailyReports.vue';
import defaultListingMixin from '@/mixins/listing/defaultListingMixin';
import { DATE_ISO } from '@/constants/dates';

export default {
  mixins: [
    updatePageContentMixin,
    defaultListingMixin,
  ],
  name: 'CheckReport',
  components: {
    FilterDailyReports,
  },
  data() {
    return {
      moment,
      isComponentLoading: false,
      isLoaded: false,
      isEditReport: false,
      isUpdateAllowed: true,
      id: null,
      date: null,
      todos: [],
      newTodo: '',
      editedTodo: null,
      visibility: 'all',
      searchParams: {
        manager: null,
        from: moment()
          .subtract(1, 'days')
          .startOf('day')
          .format(DATE_ISO),
      },
      filters: {
        all(todos) {
          return todos;
        },
        active(todos) {
          return todos.filter(todo => !todo.completed);
        },
        completed(todos) {
          return todos.filter(todo => todo.completed);
        },
      },
    };
  },
  created() {
    this.fetchData();
  },
  watch: {
    todos: {
      handler(todos) {
        if (this.isUpdateAllowed) {
          this.updateRequest();
        }
      },
      deep: true,
    },
  },
  computed: {
    account() {
      return this.$store.getters['account/account'];
    },
    filteredTodos() {
      return this.filters[this.visibility](this.todos);
    },
    remaining() {
      return this.filters.active(this.todos).length;
    },
    allDone: {
      get() {
        return this.remaining === 0;
      },
      set(value) {
        this.todos.forEach((todo) => {
          // eslint-disable-next-line no-param-reassign
          todo.completed = value;
        });
      },
    },
  },
  methods: {
    fetchData(createIfNotFound = false) {
      if (!this.searchParams.manager) {
        this.isLoaded = true;
        return;
      }
      this.isLoaded = false;
      new ManagerDailyReportProxy()
        .setParameter('date', this.searchParams.from)
        .setParameter('create_if_not_found', createIfNotFound ? 1 : 0)
        .showDaily(this.searchParams.manager.uuid)
        .then((response) => {
          this.id = response.data.data.report.id;
          this.date = response.data.data.report.date;
          this.isUpdateAllowed = false;
          if (response.data.data.report.report) {
            this.todos = response.data.data.report.report;
          } else {
            this.todos = [];
          }
          Vue.nextTick(() => {
            this.isUpdateAllowed = true;
            this.isLoaded = true;
          });
        })
        .catch(() => {
          this.id = null;
          this.todos = [];
        })
        .finally(() => {
          this.$store.dispatch('page/finishUpdateCurrentPageContent');
          this.isLoaded = true;
        });
    },
    updateRequest() {
      if (this.isLoaded) {
        this.isLoaded = false;
        new ManagerDailyReportProxy()
          .update(this.id, {
            report: this.todos,
          })
          .then((response) => {
            this.isUpdateAllowed = false;
            this.id = response.data.data.report.id;
            this.date = response.data.data.report.date;
            this.todos = response.data.data.report.report;
          })
          .finally(() => {
            Vue.nextTick(() => {
              this.isUpdateAllowed = true;
              this.isLoaded = true;
            });
          });
      }
    },

    toggleEditReport() {
      this.fetchData(!this.isEditReport);
      this.isEditReport = !this.isEditReport;
    },

    addTodo() {
      const value = this.newTodo && this.newTodo.trim();
      if (!value) {
        return;
      }
      this.todos.push({
        id: Date.now(),
        title: value,
        completed: false,
      });
      this.newTodo = '';
    },

    removeTodo(todo) {
      this.todos.splice(this.todos.indexOf(todo), 1);
    },

    doneEdit(todo) {
      if (!this.editedTodo) {
        return;
      }
      this.editedTodo = null;
      // eslint-disable-next-line no-param-reassign
      todo.title = todo.title.trim();
      if (!todo.title) {
        this.removeTodo(todo);
      }
    },

    cancelEdit(todo) {
      this.editedTodo = null;
      // eslint-disable-next-line no-param-reassign
      todo.title = this.beforeEditCache;
    },

    removeCompleted() {
      this.todos = this.filters.active(this.todos);
    },
  },
};
</script>

<style scoped>

</style>
